import {createSystem, defaultConfig, defineRecipe} from '@chakra-ui/react'

const colorScale = (hex) =>
  Object.fromEntries(
    Object.entries(hex).map(([k, v]) => [k, { value: v }]),
  )

const paletteSemantics = (name) => ({
  solid: { value: `{colors.${name}.600}` },
  contrast: { value: 'white' },
  fg: { value: `{colors.${name}.700}` },
  muted: { value: `{colors.${name}.100}` },
  subtle: { value: `{colors.${name}.50}` },
  emphasized: { value: `{colors.${name}.300}` },
  focusRing: { value: `{colors.${name}.500}` },
})

const buttonRecipe = defineRecipe({
  base: {
    fontWeight: 'bold',
    borderRadius: 'full',
  },
  variants: {
    variant: {
      solid: {
        bg: 'purple.600',
        color: 'white',
        transition: 'all 0.2s',
        _hover: {
          bg: 'purple.700',
          transform: 'scale(1.02)',
        },
        _active: {
          transform: 'scale(0.98)',
        },
      },
      outline: {
        borderColor: 'purple.600',
        borderWidth: '2px',
        color: 'purple.600',
        transition: 'all 0.2s',
        _hover: {
          bg: 'purple.50',
          transform: 'scale(1.02)',
        },
        _active: {
          transform: 'scale(0.98)',
        },
      },
    },
  },
  defaultVariants: {
    colorPalette: 'purple',
  },
})

const headingRecipe = defineRecipe({
  base: {
    fontWeight: '800',
  },
})

export const system = createSystem(defaultConfig, {
  // Chakra 3 aplica _dark con `.dark &`. Si html tiene class=dark (OS / next-themes),
  // los Alert y tokens semánticos se ponen oscuros aunque el resto de la página sea clara.
  conditions: {
    dark: '.chakra-theme.dark &',
  },
  globalCss: {
    html: {
      colorScheme: 'light',
    },
    body: {
      bg: 'white',
      color: 'gray.800',
    },
  },
  theme: {
    tokens: {
      fonts: {
        heading: { value: '"Nunito", system-ui, -apple-system, sans-serif' },
        body: { value: '"Nunito", system-ui, -apple-system, sans-serif' },
      },
      colors: {
        brand: colorScale({
          50: '#f7fafc',
          100: '#edf2f7',
          200: '#e2e8f0',
          300: '#cbd5e0',
          400: '#a0aec0',
          500: '#718096',
          600: '#4a5568',
          700: '#2d3748',
          800: '#1a202c',
          900: '#171923',
        }),
        purple: colorScale({
          50: '#f5f3ff',
          100: '#ede9fe',
          200: '#ddd6fe',
          300: '#c4b5fd',
          400: '#a78bfa',
          500: '#8b5cf6',
          600: '#7c3aed',
          700: '#6d28d9',
          800: '#5b21b6',
          900: '#4c1d95',
        }),
        gold: colorScale({
          50: '#fffbeb',
          100: '#fef3c7',
          200: '#fde68a',
          300: '#fcd34d',
          400: '#fbbf24',
          500: '#f59e0b',
          600: '#d97706',
          700: '#b45309',
          800: '#92400e',
          900: '#78350f',
        }),
        green: colorScale({
          50: '#f0fdf4',
          100: '#dcfce7',
          200: '#bbf7d0',
          300: '#86efac',
          400: '#4ade80',
          500: '#22c55e',
          600: '#16a34a',
          700: '#15803d',
          800: '#166534',
          900: '#14532d',
        }),
        red: colorScale({
          50: '#fef2f2',
          100: '#fee2e2',
          200: '#fecaca',
          300: '#fca5a5',
          400: '#f87171',
          500: '#ef4444',
          600: '#dc2626',
          700: '#b91c1c',
          800: '#991b1b',
          900: '#7f1d1d',
        }),
      },
    },
    semanticTokens: {
      colors: {
        brand: paletteSemantics('brand'),
        purple: paletteSemantics('purple'),
        gold: paletteSemantics('gold'),
        green: paletteSemantics('green'),
        red: paletteSemantics('red'),
      },
    },
    recipes: {
      button: buttonRecipe,
      heading: headingRecipe,
    },
  },
})

export default system
